import * as React from 'react';
import type { UsageLimitErrorData } from '../utils/usageLimitError';

interface UsageLimitContextType {
  showUsageLimitModal: boolean;
  usageLimitError: UsageLimitErrorData | null;
  showUsageLimitError: (errorData: UsageLimitErrorData) => void;
  hideUsageLimitError: () => void;
}

const UsageLimitContext = React.createContext<UsageLimitContextType | undefined>(undefined);

export const useUsageLimit = (): UsageLimitContextType => {
  const context = React.useContext(UsageLimitContext);
  if (context === undefined) {
    throw new Error('useUsageLimit must be used within a UsageLimitProvider');
  }
  return context;
};

interface UsageLimitProviderProps {
  children: React.ReactNode;
}

export const UsageLimitProvider: React.FC<UsageLimitProviderProps> = ({ children }) => {
  const [showUsageLimitModal, setShowUsageLimitModal] = React.useState(false);
  const [usageLimitError, setUsageLimitError] = React.useState<UsageLimitErrorData | null>(null);

  const showUsageLimitError = React.useCallback((errorData: UsageLimitErrorData) => {
    setUsageLimitError(errorData);
    setShowUsageLimitModal(true);
  }, []);

  const hideUsageLimitError = React.useCallback(() => {
    setShowUsageLimitModal(false);
    setUsageLimitError(null);
  }, []);

  return (
    <UsageLimitContext.Provider
      value={{
        showUsageLimitModal,
        usageLimitError,
        showUsageLimitError,
        hideUsageLimitError,
      }}
    >
      {children}
    </UsageLimitContext.Provider>
  );
};
